import { faCalendar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getLocalTimeZone, now, today } from "@internationalized/date";
import { forwardRef, useCallback, useState } from "react";
import {
  DatePicker as AriaDatePicker,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateField,
  DateInput,
  DateSegment,
  Heading,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Button as RivetButton } from "../button/Button";
import { Popover } from "../popover/Popover";
import { Text } from "../typography/Text";
import { NextCalendarControls, PreviousCalendarControls } from "./CalendarControls";

import type { ReactNode } from "react";
import type { DatePickerProps as AriaDatePickerProps, DateValue } from "react-aria-components";

const datePickerVariants = tv({
  slots: {
    trigger: [
      "flex w-full gap-3 rounded border border-solid text-left",
      "cursor-pointer items-center bg-field px-3 py-1 transition-colors",
      "dark:border-gray-500 dark:bg-gray-850",
    ],
    triggerText: "flex-1",
    triggerIcon: "ml-2 text-secondary dark:text-gray-400",
    popover: [
      "rounded-md border border-gray-200 bg-white shadow-xl",
      "dark:border-gray-500 dark:bg-gray-850",
    ],
    calendar: "flex flex-col gap-3 p-3",
    inputWrapper: "flex items-center gap-2",
    dateInput: [
      "flex-1 rounded-sm border border-solid border-field px-3 py-1",
      "bg-white dark:bg-gray-800",
      "dark:border-gray-700",
      "focus-within:border-field-selected focus-within:ring-2 focus-within:ring-blue-100",
      "dark:focus-within:border-blue-600 dark:focus-within:ring-blue-900",
    ],
    dateSegment: [
      "outline-hidden caret-auto px-0.5 tabular-nums",
      "text-gray-900 dark:text-gray-100",
      "data-[placeholder]:text-gray-400 dark:data-[placeholder]:text-gray-500",
    ],
    todayButton: ["rounded-sm", "dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"],
    calendarGrid: "w-full border-separate border-spacing-1 text-sm",
    weekdayHeader: [
      "size-9 text-center align-middle font-normal text-gray-700",
      "dark:text-gray-400",
    ],
    calendarCell: [
      "group relative grid size-9 cursor-pointer place-items-center rounded-full",
      "outline-hidden text-sm",
      "text-gray-900 dark:text-gray-100",
      "data-[outside-month]:text-gray-300 dark:data-[outside-month]:text-gray-600",
      "data-[hovered]:bg-blue-100 dark:data-[hovered]:bg-blue-700",
      "data-[selected]:bg-blue-600 data-[selected]:text-white",
      "dark:data-[selected]:bg-blue-500",
      "data-[unavailable]:cursor-not-allowed data-[unavailable]:text-gray-300",
      "dark:data-[unavailable]:text-gray-600",
      "data-[unavailable]:hover:bg-transparent dark:data-[unavailable]:hover:bg-transparent",
      "[&[data-selected][data-unavailable]]:bg-red-500 dark:[&[data-selected][data-unavailable]]:bg-red-700",
      "[&[data-selected][data-unavailable]]:text-white",
    ],
    todayIndicator: ["pointer-events-none absolute bottom-1 size-1.5 rounded-full"],
  },
  variants: {
    isDisabled: {
      true: {
        trigger: [
          "cursor-not-allowed opacity-50",
          "hover:border-field hover:bg-field",
          "dark:hover:border-gray-500 dark:hover:bg-gray-850",
        ],
      },
      false: {},
    },
    isOpen: {
      true: {
        trigger: [
          "border-field-selected ring-2 ring-blue-100",
          "dark:border-blue-600 dark:ring-blue-700",
        ],
      },
      false: {
        trigger: [
          "border-field hover:border-gray-300 hover:bg-gray-20",
          "dark:hover:border-gray-400 dark:hover:bg-gray-800",
        ],
      },
    },
    isSelected: {
      true: {
        todayIndicator: "bg-white",
      },
      false: {
        todayIndicator: "bg-blue-600 dark:bg-blue-500",
      },
    },
    hasValue: {
      true: {
        triggerText: "text-primary dark:text-gray-100",
      },
      false: {
        triggerText: "text-secondary dark:text-gray-400",
      },
    },
    isInvalid: {
      true: {
        trigger: ["border-red-500 ring-2 ring-red-100", "dark:border-red-500 dark:ring-red-900"],
        dateInput: ["border-red-500 ring-2 ring-red-100", "dark:border-red-500 dark:ring-red-900"],
      },
      false: {
        // no overrides
      },
    },
  },
});

export interface DatePickerProps
  extends AriaDatePickerProps<DateValue>,
    Omit<AriaDatePickerProps<DateValue>, "value" | "onChange" | "className"> {
  /** Optional class name applied to the root of the DatePicker. */
  className?: string;
  /** Controlled value. Pass `null` to clear the selection. */
  value?: DateValue | null;
  /** Error message shown when the selected date is unavailable.
   *  @default "Selected date is unavailable."
   */
  errorMessage?: string;
  /** Called when the value changes. Receives a `DateValue` or `null` when cleared. */
  onChange?: (value: DateValue | null) => void;
  /** Render content below the calendar (e.g., helper text or actions). */
  renderExtraFooter?: () => ReactNode;
  /** Predicate that marks dates as unavailable. Returning `true` disables a date
   *  and triggers the invalid state if selected.
   */
  isDateUnavailable?: (date: DateValue) => boolean;
  /** whether the datepicker modal is open */
  isOpen?: boolean;
  /** handler for when the datepicker modal is opened or closed */
  onOpenChange?: (isOpen: boolean) => void;
  /** whether to include time in the datepicker */
  includeTime?: boolean;
  /** Function to format the date and time */
  formatDateTime?: (dateObject: Date, includeTime?: boolean) => string;
  /** Whether to show the clear button. Defaults to false. */
  allowClear?: boolean;
  /** The container element in which the popover portal will be placed. Useful for fullscreen mode. */
  portalContainer?: Element;
}

export function formatDateTimeDefault(dateObject: Date, includeTime: boolean | undefined) {
  let datetimeString = dateObject.toLocaleDateString();

  if (includeTime) {
    datetimeString += " " + dateObject.toLocaleTimeString();
  }

  return datetimeString;
}

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      className,
      renderExtraFooter,
      isDateUnavailable,
      errorMessage = "Selected date is unavailable.",
      isOpen,
      onOpenChange,
      includeTime,
      formatDateTime,
      allowClear = false,
      portalContainer,
      ...props
    },
    ref
  ) => {
    const slots = datePickerVariants();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isPopoverOpen = isOpen ?? internalIsOpen;
    const setIsPopoverOpen = onOpenChange ?? setInternalIsOpen;
    const granularity = includeTime ? "minute" : "day";
    const isDisabled = props.isDisabled;

    const handleClearClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        props.onChange?.(null);
      },
      [props]
    );

    const handleClearMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleClearMouseOver = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    return (
      <AriaDatePicker ref={ref} className={className} {...props} granularity={granularity}>
        {({ state }) => {
          const invalid = Boolean(
            isDateUnavailable && state.value && isDateUnavailable(state.value)
          );
          let formattedDateTime = "Select Date";
          if (state.value) {
            const formatDateTimeFunction = formatDateTime || formatDateTimeDefault;
            formattedDateTime = formatDateTimeFunction(
              state.value.toDate(getLocalTimeZone()),
              includeTime
            );
          }
          return (
            <Popover
              placement="bottom-start"
              onOpenChange={setIsPopoverOpen}
              isOpen={isPopoverOpen}
              triggerScaleOnOpen={false}
              portalContainer={portalContainer}
            >
              <div className="flex flex-col gap-1">
                <Popover.Trigger>
                  <RivetButton
                    fullWidth
                    aria-label="Open calendar"
                    className={slots.trigger({ isOpen, isInvalid: invalid, isDisabled })}
                    isDisabled={isDisabled}
                  >
                    <span className={slots.triggerText({ hasValue: !!state.value })}>
                      {formattedDateTime}
                    </span>
                    {!state.value || !allowClear ? (
                      <FontAwesomeIcon icon={faCalendar} className={slots.triggerIcon()} />
                    ) : (
                      <RivetButton
                        variant="light"
                        size="xs"
                        className="bg-transparent p-0 hover:bg-transparent hover:text-secondary dark:bg-transparent dark:hover:bg-transparent dark:hover:text-gray-400"
                        onMouseOver={handleClearMouseOver}
                        aria-label="Clear date"
                        onClick={handleClearClick}
                        onMouseDown={handleClearMouseDown}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </RivetButton>
                    )}
                  </RivetButton>
                </Popover.Trigger>

                <Text className="text-red">{invalid ? errorMessage : ""}</Text>
              </div>
              <Popover.Content className={slots.popover()}>
                <Calendar className={slots.calendar()} isDateUnavailable={isDateUnavailable}>
                  {({ state: calendarState }) => (
                    <>
                      <header className="flex items-center justify-between ">
                        <PreviousCalendarControls calendarState={calendarState} />
                        <Heading
                          level={2}
                          className="my-auto text-center text-sm font-semibold text-primary"
                        />
                        <NextCalendarControls calendarState={calendarState} />
                      </header>
                      <div className={slots.inputWrapper()}>
                        <DateField
                          className={slots.dateInput({ isInvalid: invalid })}
                          isDateUnavailable={isDateUnavailable}
                          aria-invalid={invalid || undefined}
                        >
                          <DateInput>
                            {segment => (
                              <DateSegment segment={segment} className={slots.dateSegment()}>
                                {({ isPlaceholder, type, text }) => {
                                  if (isPlaceholder) {
                                    if (type === "month") return "MM";
                                    if (type === "day") return "DD";
                                    if (type === "year") return "YY";
                                    if (type === "hour") return "HH";
                                    if (type === "minute") return "MM";
                                  }
                                  return text;
                                }}
                              </DateSegment>
                            )}
                          </DateInput>
                        </DateField>
                        <RivetButton
                          variant="default"
                          size="md"
                          ghost
                          className={slots.todayButton()}
                          onPress={() => {
                            const valueToSet: DateValue = includeTime
                              ? now(getLocalTimeZone())
                              : today(getLocalTimeZone());
                            state.setValue(valueToSet);
                          }}
                        >
                          {includeTime ? "Now" : "Today"}
                        </RivetButton>
                      </div>
                      {invalid && <Text className="text-red">{errorMessage}</Text>}
                      <CalendarGrid weekdayStyle="short" className={slots.calendarGrid()}>
                        <CalendarGridHeader>
                          {day => (
                            <CalendarHeaderCell className={slots.weekdayHeader()}>
                              {day.slice(0, 2)}
                            </CalendarHeaderCell>
                          )}
                        </CalendarGridHeader>

                        <CalendarGridBody>
                          {date => (
                            <CalendarCell date={date} className={slots.calendarCell()}>
                              {({ formattedDate, date, isSelected }) => {
                                const isToday = date.compare(today(getLocalTimeZone())) === 0;
                                return (
                                  <>
                                    <span className="tabular-nums">{formattedDate}</span>
                                    {isToday && (
                                      <span
                                        aria-hidden
                                        className={slots.todayIndicator({ isSelected })}
                                      />
                                    )}
                                  </>
                                );
                              }}
                            </CalendarCell>
                          )}
                        </CalendarGridBody>
                      </CalendarGrid>
                    </>
                  )}
                </Calendar>
                {renderExtraFooter && <div className="w-full px-1 pb-2">{renderExtraFooter()}</div>}
              </Popover.Content>
            </Popover>
          );
        }}
      </AriaDatePicker>
    );
  }
);
DatePicker.displayName = "DatePicker";
